import React from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import type { ScheduleRequestSource } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, Text, currency, useModalControl } from '@frontend/design-system';
import { useCheckCanSendMessages } from '../../hooks/use-check-can-send-messages';
import { useScheduleRequestItemHeaderController } from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import {
  type ScheduleListItemVariant,
  ScheduleRequestItemHeaderUI,
} from '../components/schedule-request-item-header-ui';

interface Props {
  requestVersion: string;
  name: string;
  appointmentTime: string;
  personPhone: string;
  paidAmount: number;
  locationId: string;
  scheduleRequestId: string;
  variant?: ScheduleListItemVariant;
  locationChip?: (locationName: string) => React.ReactNode;
  updateSelectedScheduleRequest: () => void;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  rejectRequestModalProps?: ReturnType<typeof useModalControl>;
  onMessage: (updateStatus: boolean) => void;
  onPhoneCall?: () => void;
  requestSource: ScheduleRequestSource;
  moreInfo?: React.ReactNode;
  isV3Request: boolean;
}

export const ScheduleRequestListItemHeader = React.memo(
  ({
    requestVersion,
    name,
    paidAmount,
    appointmentTime,
    personPhone,
    locationId,
    scheduleRequestId,
    variant,
    updateSelectedScheduleRequest,
    locationChip,
    dismissRequestModalProps,
    deleteRequestModalProps,
    rejectRequestModalProps,
    onMessage,
    onPhoneCall,
    requestSource,
    moreInfo,
    isV3Request,
  }: Props) => {
    const { t } = useTranslation('schedule-request');
    const canSendMessages = useCheckCanSendMessages(locationId);

    const {
      locationName,
      appointmentTimeLabel,
      getMenuProps,
      getTriggerProps,
      handleReviewRequestMenuItemClick,
      handleRejectRequestMenuItemClick,
      handleDeleteMenuItemClick,
      handleMessageMenuItemClick,
      handleDismissMenuItemClick,
    } = useScheduleRequestItemHeaderController({
      requestVersion,
      locationId,
      scheduleRequestId,
      appointmentTime,
      variant,
      dismissRequestModalProps,
      deleteRequestModalProps,
      rejectRequestModalProps,
      onMessage,
      onPhoneCall,
      requestSource,
    });

    const handleUpdateSelectedScheduleRequest = () => handleReviewRequestMenuItemClick(updateSelectedScheduleRequest);

    const secondaryMeta = (
      <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
        {!!paidAmount ? (
          <>
            <Text textAlign='left' size='small' css={{ display: 'inline', color: theme.colors.tangerine50 }}>
              {currency(paidAmount, { decimals: 2 })}
            </Text>
            {` • `}
          </>
        ) : null}
        <Text textAlign='left' size='small'>
          {appointmentTimeLabel}
        </Text>
      </div>
    );

    const menuItems = (
      <>
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemReviewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={handleUpdateSelectedScheduleRequest}
        >
          {t('Review Request')}
        </PopoverMenuItem>
        {personPhone && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
            Icon={() => <Icon name='message-small' />}
            disabled={!canSendMessages}
            hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
            onClick={handleMessageMenuItemClick}
          >
            {t('Message')}
          </PopoverMenuItem>
        )}
        {isV3Request && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemRejectBtn}
            Icon={() => <Icon name='calendar-discarded-small' />}
            onClick={handleRejectRequestMenuItemClick}
          >
            {t('Reject Request')}
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
          Icon={() => <Icon name={isV3Request ? 'trash-small' : 'remove2-small'} />}
          onClick={isV3Request ? handleDeleteMenuItemClick : handleDismissMenuItemClick}
        >
          {isV3Request ? t('Delete') : t('Dismiss')}
        </PopoverMenuItem>
      </>
    );

    return (
      <ScheduleRequestItemHeaderUI
        name={name}
        locationName={locationName}
        secondaryMeta={secondaryMeta}
        moreInfo={moreInfo}
        menuItems={menuItems}
        locationChip={locationChip}
        getTriggerProps={getTriggerProps}
        getMenuProps={getMenuProps}
        variant={variant}
        onClick={variant === 'overlappingPanel' ? handleUpdateSelectedScheduleRequest : undefined}
      />
    );
  }
);

ScheduleRequestListItemHeader.displayName = 'ScheduleRequestListItemHeader';
